
import React, { useMemo, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { phpApi } from "@/integrations/php/client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2,
  AtSign,
  Phone,
  Calendar,
  Shield,
  Loader2,
  UserCog,
  Search,
} from "lucide-react";
import { PageHeader } from "@/components/layouts/PageHeader";
import { StatCard } from "@/components/ui/StatCard";
import { EmptyState } from "@/components/ui/EmptyState";

interface Admin {
  id: string;
  username: string;
  email: string;
  mobile: string;
  active: boolean;
  created_at: string;
}

const Admins = () => {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [isFetching, setIsFetching] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    mobile: ""
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [showNewAdminDialog, setShowNewAdminDialog] = useState(false);
  const { toast } = useToast();
  const [deleteAdminId, setDeleteAdminId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setIsFetching(true);
      const { data, error } = await phpApi
        .from('administrators')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAdmins(data || []);
    } catch (error) {
      console.error('Error fetching administrators:', error);
      toast({
        variant: "destructive",
        title: "Σφάλμα",
        description: "Αποτυχία φόρτωσης διαχειριστών.",
      });
    } finally {
      setIsFetching(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewAdmin({ ...newAdmin, [name]: value });
    setFormError(null);
  };

  const validateForm = () => {
    if (!newAdmin.username || !newAdmin.email || !newAdmin.password || !newAdmin.mobile) {
      setFormError("Παρακαλώ συμπληρώστε όλα τα πεδία");
      return false;
    }

    if (newAdmin.password !== newAdmin.confirmPassword) {
      setFormError("Οι κωδικοί δεν ταιριάζουν");
      return false;
    }

    if (newAdmin.password.length < 6) {
      setFormError("Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newAdmin.email)) {
      setFormError("Παρακαλώ εισάγετε έγκυρο email");
      return false;
    }

    return true;
  };

  const handleCreateAdmin = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      const { data, error } = await phpApi.functions.invoke('create-admin', {
        body: {
          username: newAdmin.username,
          email: newAdmin.email,
          password: newAdmin.password,
          mobile: newAdmin.mobile,
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error('Σφάλμα δημιουργίας διαχειριστή');
      }

      if (!data.success) {
        setFormError(data.message || 'Σφάλμα δημιουργίας διαχειριστή');
        return;
      }

      toast({
        title: "Επιτυχία",
        description: "Ο διαχειριστής δημιουργήθηκε επιτυχώς",
      });

      setNewAdmin({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        mobile: ""
      });

      setShowNewAdminDialog(false);
      fetchAdmins();
    } catch (error) {
      console.error('Error creating administrator:', error);
      toast({
        variant: "destructive",
        title: "Σφάλμα",
        description: "Αποτυχία δημιουργίας διαχειριστή.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (adminId: string) => {
    setDeleteAdminId(adminId);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteAdminId) return;

    try {
      setIsDeleting(true);

      const { error: credError } = await phpApi
        .from('admin_credentials')
        .delete()
        .eq('admin_id', deleteAdminId);

      if (credError) throw credError;

      const { error: adminError } = await phpApi
        .from('administrators')
        .delete()
        .eq('id', deleteAdminId);

      if (adminError) throw adminError;

      toast({
        title: "Επιτυχία",
        description: "Ο διαχειριστής διαγράφηκε επιτυχώς",
      });

      fetchAdmins();
      setShowDeleteDialog(false);
      setDeleteAdminId(null);
    } catch (error) {
      console.error('Error deleting administrator:', error);
      toast({
        variant: "destructive",
        title: "Σφάλμα",
        description: "Αποτυχία διαγραφής διαχειριστή.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const activeAdminsCount = useMemo(() => admins.filter(a => a.active).length, [admins]);
  const inactiveAdminsCount = admins.length - activeAdminsCount;

  const createdThisMonth = useMemo(() => {
    const now = new Date();
    return admins.filter(admin => {
      const created = new Date(admin.created_at);
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
    }).length;
  }, [admins]);

  const filteredAdmins = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return admins.filter(admin => {
      const matchesSearch =
        !normalizedSearch ||
        admin.username.toLowerCase().includes(normalizedSearch) ||
        admin.email.toLowerCase().includes(normalizedSearch) ||
        admin.mobile.includes(normalizedSearch);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && admin.active) ||
        (statusFilter === "inactive" && !admin.active);

      return matchesSearch && matchesStatus;
    });
  }, [admins, searchTerm, statusFilter]);

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Διαχειριστές"
        description="Διαχείριση πρόσβασης συστήματος"
        icon={<UserCog />}
        iconGradient="rose"
        actions={
          <Button onClick={() => setShowNewAdminDialog(true)} className="btn-gradient">
            <Plus className="h-4 w-4 mr-2" />
            Νέος Διαχειριστής
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard value={admins.length}        title="Συνολικά"   icon={<Shield />}      variant="danger"   size="compact" />
        <StatCard value={activeAdminsCount}    title="Ενεργοί"    icon={<CheckCircle />} variant="success"  size="compact" />
        <StatCard value={inactiveAdminsCount}  title="Ανενεργοί"  icon={<AlertCircle />} variant="default"  size="compact" />
        <StatCard value={createdThisMonth}     title="Νέοι μήνα"  icon={<Calendar />}    variant="info"     size="compact" />
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Αναζήτηση με username, email ή κινητό..."
              className="pl-10 bg-slate-50 border-slate-200"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {([
              { key: "all"      as const, label: "Όλοι",       count: admins.length },
              { key: "active"   as const, label: "Ενεργοί",    count: activeAdminsCount },
              { key: "inactive" as const, label: "Ανενεργοί",  count: inactiveAdminsCount },
            ]).map(({ key, label, count }) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-all ${
                  statusFilter === key
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {label}
                <span className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${statusFilter === key ? "bg-white/20 text-white" : "bg-white text-slate-600"}`}>
                  {count}
                </span>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Alert className={activeAdminsCount <= 1 ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}>
        <AlertCircle className={activeAdminsCount <= 1 ? "text-amber-600" : "text-emerald-600"} />
        <AlertTitle>{activeAdminsCount <= 1 ? "Χαμηλή κάλυψη πρόσβασης" : "Κάλυψη πρόσβασης επαρκής"}</AlertTitle>
        <AlertDescription>
          {activeAdminsCount <= 1
            ? "Προτείνεται τουλάχιστον 2 ενεργοί διαχειριστές για αποφυγή single-point-of-failure."
            : "Υπάρχουν αρκετοί ενεργοί διαχειριστές για ασφαλή λειτουργία."}
        </AlertDescription>
      </Alert>

      {isFetching && (
        <div className="flex justify-center p-12">
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin text-rose-600 mx-auto" />
            <p className="mt-4 text-slate-500">Φόρτωση διαχειριστών...</p>
          </div>
        </div>
      )}

      {/* Admin Cards */}
      {!isFetching && filteredAdmins.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filteredAdmins.map((admin) => (
            <Card key={admin.id} className="group border-slate-200 hover:border-rose-200 hover:shadow-lg transition-all duration-300">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center text-white font-bold text-sm">
                      {admin.username.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{admin.username}</CardTitle>
                      <Badge
                        variant={admin.active ? "default" : "secondary"}
                        className={admin.active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : ""}
                      >
                        {admin.active ? "Ενεργός" : "Ανενεργός"}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-2">
                <div className="flex items-center text-sm text-slate-600">
                  <AtSign className="h-4 w-4 mr-2 text-slate-400" />
                  {admin.email}
                </div>
                <div className="flex items-center text-sm text-slate-600">
                  <Phone className="h-4 w-4 mr-2 text-slate-400" />
                  {admin.mobile}
                </div>
                <div className="flex items-center text-sm text-slate-500">
                  <Calendar className="h-4 w-4 mr-2 text-slate-400" />
                  {new Date(admin.created_at).toLocaleDateString('el-GR')}
                </div>
              </CardContent>

              <CardFooter className="pt-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeleteClick(admin.id)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Διαγραφή
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {filteredAdmins.length === 0 && !isFetching && (
        <EmptyState
          icon={<Shield />}
          title="Δεν βρέθηκαν διαχειριστές"
          description={searchTerm ? "Δοκιμάστε διαφορετικά φίλτρα." : "Προσθέστε τον πρώτο διαχειριστή."}
          action={<Button onClick={() => setShowNewAdminDialog(true)} className="btn-gradient"><Plus className="h-4 w-4 mr-2" />Νέος Διαχειριστής</Button>}
        />
      )}

      {/* New Admin Dialog */}
      <Dialog open={showNewAdminDialog} onOpenChange={setShowNewAdminDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Νέος Διαχειριστής</DialogTitle>
            <DialogDescription>
              Συμπληρώστε τα στοιχεία του νέου διαχειριστή
            </DialogDescription>
          </DialogHeader>

          {formError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Σφάλμα</AlertTitle>
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Όνομα χρήστη</Label>
              <Input
                id="username"
                name="username"
                value={newAdmin.username}
                onChange={handleInputChange}
                className="bg-slate-50 border-slate-200"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={newAdmin.email}
                onChange={handleInputChange}
                className="bg-slate-50 border-slate-200"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="mobile">Κινητό</Label>
              <Input
                id="mobile"
                name="mobile"
                value={newAdmin.mobile}
                onChange={handleInputChange}
                className="bg-slate-50 border-slate-200"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Κωδικός</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={newAdmin.password}
                onChange={handleInputChange}
                className="bg-slate-50 border-slate-200"
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Επιβεβαίωση κωδικού</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={newAdmin.confirmPassword}
                onChange={handleInputChange}
                className="bg-slate-50 border-slate-200"
                autoComplete="new-password"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewAdminDialog(false)}>
              Άκυρο
            </Button>
            <Button onClick={handleCreateAdmin} disabled={isSubmitting} className="btn-gradient">
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Δημιουργία...
                </>
              ) : (
                "Δημιουργία"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Διαγραφή Διαχειριστή</DialogTitle>
            <DialogDescription>
              Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτόν τον διαχειριστή;
              Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Άκυρο
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Διαγραφή...
                </>
              ) : (
                "Διαγραφή"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admins;
